/**
 * AddDocumentsPanel — right-anchored fixed overlay listing available import sources.
 *
 * No external library: pure CSS-in-JS using Carbon design tokens.
 */
import { IconButton } from '@carbon/react';
import { Close, Upload, DocumentImport, ChevronRight, type CarbonIconType } from '@carbon/icons-react';

export interface AddDocumentsPanelProps {
  open: boolean;
  onClose: () => void;
  onLocalFile: () => void;
  onImportFromKb: () => void;
}

const sources: {
  icon: CarbonIconType;
  title: string;
  subtitle: string;
  onAction: 'local' | 'kb';
}[] = [
  {
    icon: Upload,
    title: 'Local file',
    subtitle: 'Upload from your computer',
    onAction: 'local',
  },
  {
    icon: DocumentImport,
    title: 'Knowledge Base',
    subtitle: 'Copy documents from another KB',
    onAction: 'kb',
  },
];

export function AddDocumentsPanel({
  open,
  onClose,
  onLocalFile,
  onImportFromKb,
}: AddDocumentsPanelProps) {
  if (!open) return null;

  const handleSource = (action: 'local' | 'kb') => {
    if (action === 'local') {
      onLocalFile();
    } else {
      onImportFromKb();
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.2)',
          zIndex: 8999,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '320px',
          background: 'var(--cds-layer-01)',
          borderLeft: '1px solid var(--cds-border-subtle)',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
          zIndex: 9000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            borderBottom: '1px solid var(--cds-border-subtle)',
          }}
        >
          <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--cds-text-primary)' }}>
            Add Documents
          </span>
          <IconButton kind="ghost" size="sm" label="Close panel" onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        {/* Source rows */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {sources.map(({ icon: Icon, title, subtitle, onAction }) => (
            <SourceRow
              key={onAction}
              icon={<Icon size={20} />}
              title={title}
              subtitle={subtitle}
              onClick={() => handleSource(onAction)}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function SourceRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--cds-layer-hover-01)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '';
      }}
    >
      {/* Icon badge */}
      <div
        style={{
          width: '40px',
          height: '40px',
          flexShrink: 0,
          background: 'var(--cds-layer-02)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cds-text-primary)',
        }}
      >
        {icon}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: 'var(--cds-text-primary)', fontWeight: 500 }}>{title}</div>
        <div style={{ color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>{subtitle}</div>
      </div>

      {/* Chevron */}
      <ChevronRight size={16} style={{ color: 'var(--cds-text-secondary)', flexShrink: 0 }} />
    </div>
  );
}
